import { HiltiDocumentPrices } from "../components/HiltiInvoicePrices";
import HistoryTable from "../components/HistoryTable";
import { useAssignmentWorklist } from "../api/articleAssignments";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  BELEG_DOC_TYPE_LABEL,
  fetchBelegDetail,
  recordBelegCreditLinkEvent,
  recordBelegPriceAnomalyReview,
} from "../api/belege";
import { fetchCompletePriceHistory } from "../api/articles";
import type {
  BelegDetailResponse,
  BelegLineItem,
  BelegPriceAnomalyAction,
  BelegPriceAnomalyReviewEvent,
  BelegPriceAnomalyStatus,
  BelegRow,
  CreditAnomalyLink,
} from "../api/belege";
import type { PriceHistoryRow } from "../api/articles";
import HistoryExternalNumber from "../components/HistoryExternalNumber";
import BelegDetailHeader from "../components/BelegDetailHeader";
import FigureRow from "../components/FigureRow";
import BelegReviewStates from "../components/BelegReviewStates";
import { fetchProjects } from "../api/projects";
import type { ProjectSummary } from "../api/projects";
import { DetailBackNav, DetailQueryStatus } from "../components/DetailPageNav";
import ArticleAssignment from "../components/ArticleAssignment";
import DocumentRecheckHistory from "../components/DocumentRecheckHistory";
import CostCenterValue from "../components/CostCenterValue";
import HiltiCostCenterReviewPanel from "../components/HiltiCostCenterReviewPanel";
import CreditCostCenterReviewPanel from "../components/CreditCostCenterReviewPanel";
import { useSession } from "../auth/useSession";
import DocumentPdfActions from "../components/DocumentPdfActions";
import { isDemoMode } from "../lib/env";
import {
  backTargetFromLocationState,
  type DetailLocationState,
} from "../lib/backNavigation";
import { parseBelegReviewUrl } from "../lib/belegReviewLinks";
import { cleanDescription } from "../lib/description";
import { historyCsv, historyCsvNumber } from "../lib/historyCsv";
import BelegReviewTimeline from "../components/BelegReviewTimeline";
import { downloadOfferCsv } from "../lib/offerCsv";
import {
  costCenterHref,
  costCenterHrefFromProjectKey,
  deliveryAddressCostCenterHref,
  displayCostCenterKey,
  costCenterTypeFromProjectKey,
} from "../lib/costCenterDisplay";
import {
  formatDate,
  formatEuro,
  formatPercent,
  formatQty,
  toNumber,
  formatSignedPercent,
} from "../lib/format";
import {
  useContextualHelpGuide,
  useHelpGuideLauncher,
} from "../lib/helpContext";
import { priceVarianceHref } from "../lib/priceVarianceLinks";
import { projectDisplay } from "../lib/projectDisplay";
import { reviewStatusLabel } from "../lib/reviewStatusLabels";
import { useRecordRecentlyViewed } from "../lib/recentlyViewed";
import SectionInfo from "../components/SectionInfo";

export default function BelegDetailPage() {
  const queryClient = useQueryClient();
  const { belegId } = useParams<{ belegId: string }>();
  const id = belegId ?? "";
  const location = useLocation();
  const navigate = useNavigate();
  const canManageCostCenters = useSession().user?.active_membership.can_manage_cost_centers ?? false;
  const openHelpGuide = useHelpGuideLauncher();
  const reviewUrlTarget = useMemo(
    () => parseBelegReviewUrl(location.search),
    [location.search],
  );
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const fromState = (location.state as DetailLocationState)?.from;
  const back = backTargetFromLocationState({
    from: reviewUrlTarget.returnTo ?? fromState,
    fallback: { to: "/belege", label: "← zurück zu den Belegen" },
  });

  const detailQuery = useQuery({
    queryKey: ["belege", "detail", id],
    queryFn: () => fetchBelegDetail(id),
    enabled: id.length > 0,
    refetchOnMount: reviewUrlTarget.kind === "none" ? undefined : "always",
  });

  const detail = detailQuery.data;
  const beleg = detail?.beleg ?? null;
  const lines = detail?.lines ?? [];
  const costCenterReviewLinePos = location.hash === "#kostenstelle-pruefen"
    ? String(lines.find((line) => !line.has_cost_center_context)?.line_pos ?? "") || null
    : location.hash.startsWith("#kostenstelle-pruefen-")
      ? decodeURIComponent(location.hash.slice("#kostenstelle-pruefen-".length))
      : null;
  const firstMissingCostCenterLine = (beleg?.doc_type === "gutschrift" || (beleg?.supplier === "HILTI" && beleg.doc_type === "rechnung"))
    ? lines.find((line) => !line.has_cost_center_context && line.line_pos != null)
    : null;
  useEffect(() => {
    if (location.hash !== "#kostenstelle-pruefen" || firstMissingCostCenterLine?.line_pos == null) return;
    navigate(`${location.pathname}${location.search}#kostenstelle-pruefen-${encodeURIComponent(String(firstMissingCostCenterLine.line_pos))}`, {
      replace: true,
      state: location.state,
    });
  }, [firstMissingCostCenterLine?.line_pos, location.hash, location.pathname, location.search, location.state, navigate]);
  useEffect(() => {
    if (!costCenterReviewLinePos || !detail) return;
    const frame = window.requestAnimationFrame(() =>
      document.getElementById("kostenstelle-pruefen")?.scrollIntoView({ block: "start" }),
    );
    return () => window.cancelAnimationFrame(frame);
  }, [costCenterReviewLinePos, detail]);
  const projectsQuery = useQuery({
    queryKey: ["projects", "beleg-detail", "all"],
    queryFn: () =>
      fetchProjects({ activeOnly: false, limit: 1000, withOverpay: false }),
    staleTime: 300_000,
    enabled: Boolean(beleg),
  });
  const projectByKey = useMemo<ReadonlyMap<string, ProjectSummary>>(
    () =>
      new Map(
        (projectsQuery.data?.items ?? []).map((project) => [
          project.project_key,
          project,
        ]),
      ),
    [projectsQuery.data],
  );
  const title = beleg?.doc_number ?? id;
  useRecordRecentlyViewed(
    beleg
      ? {
          kind: "beleg",
          key: id,
          label: title,
          detail: [BELEG_DOC_TYPE_LABEL[beleg.doc_type], beleg.supplier].filter(Boolean).join(" · ") || null,
        }
      : null,
  );
  const buyer = beleg?.buyer_name ?? beleg?.buyer_email;
  const sourceCounts = useMemo(() => countSources(beleg), [beleg]);
  const defaultVarianceHref = beleg
    ? priceVarianceHref({
        supplier: beleg.supplier ? [beleg.supplier] : undefined,
        online_user: beleg.buyer_email ?? undefined,
      })
    : null;
  const varianceHref = reviewUrlTarget.returnTo?.startsWith("/reports/variance")
    ? reviewUrlTarget.returnTo
    : defaultVarianceHref;
  const anomalyLines = useMemo(
    () => lines.filter((line) => line.is_price_anomaly),
    [lines],
  );
  const creditLinks = detail?.credit_links ?? [];
  const standaloneCreditLinks = useMemo(() => {
    if (beleg?.doc_type === "gutschrift") return creditLinks;
    const activePositions = new Set(
      anomalyLines.map((line) => String(line.line_pos ?? "")),
    );
    return creditLinks.filter(
      (link) => !activePositions.has(String(link.invoice_line_pos)),
    );
  }, [anomalyLines, beleg?.doc_type, creditLinks]);
  const reviewEvents = detail?.review_events;
  const historicalReviewEvents = useMemo(() => {
    const activeLineKeys = new Set(anomalyLines.map((line) => lineReviewKey(line)));
    return (reviewEvents ?? []).filter(
      (event) =>
        !activeLineKeys.has(
          lineReviewKeyFromValues(event.invoice_doc_id, event.line_pos),
        ),
    );
  }, [anomalyLines, reviewEvents]);
  const [selectedReviewLineKey, setSelectedReviewLineKey] = useState<
    string | null
  >(null);
  const selectedReviewLine = useMemo(
    () =>
      anomalyLines.find((line) => lineReviewKey(line) === selectedReviewLineKey) ??
      null,
    [anomalyLines, selectedReviewLineKey],
  );
  useContextualHelpGuide(
    selectedReviewLine ? "price-anomaly-review" : null,
  );
  const reviewTargetIssue = useMemo(() => {
    if (!detail || reviewUrlTarget.kind === "none") return null;
    if (reviewUrlTarget.kind === "invalid") {
      return {
        title: "Preisprüfung nicht verfügbar",
        message:
          "Der angeforderte Positionsverweis ist ungültig. Es wurde keine andere Position ausgewählt.",
      };
    }
    const requestedLine = lines.find(
      (line) => String(line.line_pos ?? "") === reviewUrlTarget.linePos,
    );
    if (!requestedLine) {
      return {
        title: "Preisprüfung nicht verfügbar",
        message: `Position ${reviewUrlTarget.linePos} ist in diesem Beleg nicht vorhanden. Es wurde keine andere Position ausgewählt.`,
      };
    }
    if (!requestedLine.is_price_anomaly) {
      return {
        title: "Preisprüfung nicht mehr offen",
        message: `Position ${reviewUrlTarget.linePos} ist keine Preisauffälligkeit mehr. Es wurde keine andere Position ausgewählt.`,
      };
    }
    return null;
  }, [detail, lines, reviewUrlTarget]);
  const appliedReviewTargetRef = useRef<{
    token: string;
    outcome: "applied" | "unavailable" | "invalid";
  } | null>(null);
  const [reviewError, setReviewError] = useState<ReviewError | null>(null);
  const [reviewSuccess, setReviewSuccess] = useState<ReviewSuccess | null>(null);
  const reviewMutationLock = useRef(false);
  const reviewMutation = useMutation({
    mutationFn: recordBelegPriceAnomalyReview,
    onMutate: () => {
      setReviewError(null);
      setReviewSuccess(null);
    },
    onSuccess: (data, variables) => {
      const lineKey = lineReviewKeyFromValues(variables.belegId, variables.linePos);
      let nextLine: BelegLineItem | null = null;
      let allCompleted = false;
      setReviewError(null);
      const queryKey = ["belege", "detail", variables.belegId] as const;
      const current = queryClient.getQueryData<BelegDetailResponse>(queryKey);
      const updatedLines = current?.lines.map((line) =>
        lineReviewKey(line) === lineKey ? data.line : line,
      );
      if (updatedLines && isAdvancingReviewAction(variables.action)) {
        const reviewLines = updatedLines.filter((line) => line.is_price_anomaly);
        const currentIndex = reviewLines.findIndex(
          (line) => lineReviewKey(line) === lineKey,
        );
        if (currentIndex >= 0) {
          const followingLines = [
            ...reviewLines.slice(currentIndex + 1),
            ...reviewLines.slice(0, currentIndex),
          ];
          nextLine =
            followingLines.find(
              (line) => (line.price_anomaly_status ?? "open") === "open",
            ) ?? null;
          allCompleted =
            nextLine === null &&
            reviewLines.every(
              (line) => (line.price_anomaly_status ?? "open") !== "open",
            );
        }
      }
      if (current && updatedLines) {
        queryClient.setQueryData<BelegDetailResponse>(queryKey, {
          ...current,
          beleg: data.beleg,
          lines: updatedLines,
          review_events: [data.event, ...(current.review_events ?? [])],
        });
      }
      setReviewSuccess({
        savedLinePos: String(variables.linePos),
        nextLinePos: nextLine ? String(nextLine.line_pos ?? "") : null,
        allCompleted,
      });
      if (nextLine) {
        setSelectedReviewLineKey(lineReviewKey(nextLine));
        window.requestAnimationFrame(() => focusPriceReviewPanel());
      }
      void queryClient.invalidateQueries({ queryKey: ["belege"] });
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (error: Error, variables) => {
      setReviewSuccess(null);
      setReviewError({
        lineKey: lineReviewKeyFromValues(variables.belegId, variables.linePos),
        message: error.message,
      });
    },
    onSettled: () => {
      reviewMutationLock.current = false;
    },
  });
  const [creditLinkFeedback, setCreditLinkFeedback] =
    useState<CreditLinkFeedback | null>(null);
  const creditLinkMutation = useMutation({
    mutationFn: recordBelegCreditLinkEvent,
    onMutate: (variables) => {
      setCreditLinkFeedback({
        linkId: variables.linkId,
        tone: "pending",
        message: "Speichere …",
      });
    },
    onSuccess: (data, variables) => {
      setCreditLinkFeedback({
        linkId: data.link.link_id,
        tone: "success",
        message:
          variables.action === "confirmed"
            ? data.link.link_id === variables.linkId
              ? "Zuordnung bestätigt"
              : "Teilzuordnung bestätigt"
            : "Zuordnung aufgehoben",
      });
      queryClient.setQueryData<BelegDetailResponse>(
        ["belege", "detail", variables.belegId],
        (current) => {
          if (!current) return current;
          const returnedLinks = [data.superseded_link, data.link].filter(
            (link): link is CreditAnomalyLink => link != null,
          );
          const returnedById = new Map(
            returnedLinks.map((link) => [link.link_id, link]),
          );
          const currentIds = new Set(
            current.credit_links.map((link) => link.link_id),
          );
          return {
            ...current,
            credit_links: [
              ...current.credit_links.map(
                (link) => returnedById.get(link.link_id) ?? link,
              ),
              ...returnedLinks.filter((link) => !currentIds.has(link.link_id)),
            ],
          };
        },
      );
      const counterpartId =
        data.link.credit_doc_id === variables.belegId
          ? data.link.invoice_doc_id
          : data.link.credit_doc_id;
      void queryClient.invalidateQueries({
        queryKey: ["belege", "detail", variables.belegId],
      });
      void queryClient.invalidateQueries({
        queryKey: ["belege", "detail", counterpartId],
      });
      void queryClient.invalidateQueries({ queryKey: ["belege"] });
    },
    onError: (error: Error, variables) => {
      setCreditLinkFeedback({
        linkId: variables.linkId,
        tone: "error",
        message: error.message,
      });
    },
  });

  function recordReview(
    row: BelegLineItem,
    action: BelegPriceAnomalyAction,
    comment: string,
  ) {
    if (
      row.line_pos === null ||
      reviewMutationLock.current ||
      reviewMutation.isPending
    ) {
      return;
    }
    reviewMutationLock.current = true;
    reviewMutation.mutate({
      belegId: row.beleg_id,
      linePos: row.line_pos,
      action,
      comment,
    });
  }

  function selectReviewLine(lineKey: string) {
    if (reviewMutationLock.current || reviewMutation.isPending) return;
    setReviewSuccess(null);
    setSelectedReviewLineKey(lineKey);
    focusReviewEditor();
  }

  function recordCreditLinkAction(
    link: CreditAnomalyLink,
    action: "confirmed" | "revoked",
  ) {
    if (!beleg) return;
    creditLinkMutation.mutate({
      belegId: beleg.beleg_id,
      linkId: link.link_id,
      action,
      reason:
        action === "confirmed"
          ? "Zuordnung in der Belegprüfung bestätigt."
          : "Zuordnung in der Belegprüfung aufgehoben.",
    });
  }

  useEffect(() => {
    if (!detail) return;
    if (reviewUrlTarget.kind === "none") {
      appliedReviewTargetRef.current = null;
      return;
    }
    const targetToken = reviewTargetToken(id, reviewUrlTarget);
    const previousAttempt = appliedReviewTargetRef.current;
    if (reviewUrlTarget.kind === "invalid") {
      if (
        previousAttempt?.token === targetToken &&
        previousAttempt.outcome === "invalid"
      ) {
        return;
      }
      appliedReviewTargetRef.current = {
        token: targetToken,
        outcome: "invalid",
      };
      setSelectedReviewLineKey(null);
      return;
    }
    const requestedLine = anomalyLines.find(
      (line) => String(line.line_pos ?? "") === reviewUrlTarget.linePos,
    );
    if (requestedLine) {
      if (
        previousAttempt?.token === targetToken &&
        previousAttempt.outcome === "applied"
      ) {
        return;
      }
      appliedReviewTargetRef.current = {
        token: targetToken,
        outcome: "applied",
      };
      setSelectedReviewLineKey(lineReviewKey(requestedLine));
      focusReviewEditor();
      return;
    }
    if (previousAttempt?.token === targetToken) return;
    appliedReviewTargetRef.current = {
      token: targetToken,
      outcome: "unavailable",
    };
    setSelectedReviewLineKey(null);
  }, [anomalyLines, detail, id, reviewUrlTarget]);

  useEffect(() => {
    if (reviewUrlTarget.kind !== "none") return;
    if (anomalyLines.length === 0) {
      if (selectedReviewLineKey !== null) setSelectedReviewLineKey(null);
      return;
    }
    if (
      selectedReviewLineKey &&
      anomalyLines.some((line) => lineReviewKey(line) === selectedReviewLineKey)
    ) {
      return;
    }
    const requestedCreditAnchor = locationHashTarget(location.hash);
    const requestedCreditLink =
      location.hash.startsWith("#gutschrift-zuordnung-")
        ? creditLinks.find(
            (link) =>
              creditLinkAnchorId(link) === requestedCreditAnchor,
          )
        : null;
    const linkedLine =
      location.hash === "#gutschrift-zuordnungen" || requestedCreditLink
        ? anomalyLines.find((line) =>
            creditLinks.some(
              (link) =>
                (!requestedCreditLink ||
                  link.link_id === requestedCreditLink.link_id) &&
                String(link.invoice_line_pos) === String(line.line_pos ?? ""),
            ),
          )
        : null;
    const next =
      linkedLine ??
      anomalyLines.find((line) => (line.price_anomaly_status ?? "open") === "open") ??
      anomalyLines[0];
    setSelectedReviewLineKey(lineReviewKey(next));
  }, [
    anomalyLines,
    creditLinks,
    location.hash,
    reviewUrlTarget.kind,
    selectedReviewLineKey,
  ]);

  useEffect(() => {
    if (
      !beleg ||
      (location.hash !== "#preisauffaelligkeiten" &&
        location.hash !== "#gutschrift-zuordnungen" &&
        !location.hash.startsWith("#gutschrift-zuordnung-"))
    ) {
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      document
        .getElementById(
          location.hash === "#gutschrift-zuordnungen"
            ? "gutschrift-zuordnungen"
            : location.hash.startsWith("#gutschrift-zuordnung-")
              ? locationHashTarget(location.hash)
              : selectedReviewLine
                ? "preispruefung-editor"
                : "belegpositionen",
        )
        ?.scrollIntoView({ block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [beleg, location.hash, selectedReviewLine]);

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <DetailBackNav back={back} home={{ to: "/belege", label: "Belegübersicht" }} />

      <DetailQueryStatus
        isLoading={detailQuery.isLoading}
        error={detailQuery.isError ? detailQuery.error : null}
        loadingLabel="Lade Beleg …"
        notFoundLabel="Beleg nicht gefunden."
      />

      {beleg ? (
        <>
          <BelegDetailHeader
            docType={beleg.doc_type}
            docId={beleg.beleg_id}
            docNumber={beleg.doc_number}
            docDate={beleg.doc_date}
            supplier={beleg.supplier}
            buyer={buyer}
            pdfAvailable={beleg.pdf_available}
            pdfDocId={beleg.pdf_doc_id}
            positions={lines.map((line) => ({
              projectKey: line.project_key,
              deliveryAddressDisplay: line.delivery_address_display,
            }))}
            projectByKey={projectByKey}
            status={<BelegReviewStates beleg={beleg} />}
          />
          <div className="mt-4">
            <FigureRow
              figures={[
                {
                  label: beleg.doc_type === "gutschrift" ? "Gutschriftbetrag" : "Belegbetrag",
                  value: formatEuro(beleg.total_amount),
                  hint: beleg.doc_type === "gutschrift" ? `Quellbetrag ${formatEuro(beleg.source_total_amount)}` : undefined,
                },
                {
                  label: "Positionen",
                  value: formatQty(beleg.line_count),
                  hint: countLabel(beleg.article_count, "Artikel", "Artikel"),
                },
                ...(toNumber(beleg.open_overpay_abs) ?? 0) > 0
                  ? [{
                      label: "Offene Mehrkosten",
                      value: formatEuro(beleg.open_overpay_abs),
                      hint: countLabel(anomalyLines.length, "offene Preisprüfung", "offene Preisprüfungen"),
                      tone: "negative" as const,
                    }]
                  : [],
              ]}
            />
          </div>

          {reviewTargetIssue ? (
            <RequestedReviewTargetNotice
              title={reviewTargetIssue.title}
              message={reviewTargetIssue.message}
            />
          ) : null}

          {beleg.supplier === "HILTI" && <HiltiDocumentPrices key={`hilti:${id}`} docId={id} />}

          <BelegPositionsSection
            rows={lines}
            docNumber={title}
            fromPath={currentPath}
            projectByKey={projectByKey}
            selectedLineKey={selectedReviewLine ? lineReviewKey(selectedReviewLine) : null}
            reviewSelectionDisabled={reviewMutation.isPending}
            onSelectReview={selectReviewLine}
            reviewableCostCenter={(beleg.doc_type === "gutschrift" || (beleg.supplier === "HILTI" && beleg.doc_type === "rechnung")) && canManageCostCenters}
            costCenterDecisions={detail?.cost_center_decisions ?? {}}
          />

          {beleg.doc_type === "gutschrift" && canManageCostCenters && costCenterReviewLinePos ? (
            lines.some((line) => String(line.line_pos) === costCenterReviewLinePos) ? (
              <CreditCostCenterReviewPanel
                key={`${id}:${costCenterReviewLinePos}`}
                docId={id}
                docNumber={title}
                linePos={costCenterReviewLinePos}
                onClose={() => navigate(`${location.pathname}${location.search}`, { state: location.state })}
              />
            ) : <RequestedReviewTargetNotice title="Kostenstellenprüfung nicht verfügbar" message="Diese Position ist im Beleg nicht mehr vorhanden." />
          ) : null}

          {beleg.supplier === "HILTI" && beleg.doc_type === "rechnung" && canManageCostCenters && costCenterReviewLinePos ? (
            lines.some(line => String(line.line_pos) === costCenterReviewLinePos) ? <HiltiCostCenterReviewPanel
              key={`hilti-review:${id}:${costCenterReviewLinePos}`} docId={id} docNumber={title} linePos={costCenterReviewLinePos}
              onClose={() => navigate(`${location.pathname}${location.search}`, { state: location.state })}
              nextHref={(() => { const next=lines.find(line=>!line.has_cost_center_context && String(line.line_pos)!==costCenterReviewLinePos); return next ? `${location.pathname}${location.search}#kostenstelle-pruefen-${encodeURIComponent(String(next.line_pos))}` : null; })()}
            /> : <RequestedReviewTargetNotice title="Kostenstellenprüfung nicht verfügbar" message="Diese Position ist im Beleg nicht mehr vorhanden." />
          ) : null}

          <DocumentRecheckHistory key={id} docId={id} />

          <BelegStatusChips
            beleg={beleg}
            sourceCounts={sourceCounts}
            varianceHref={anomalyLines.length === 0 ? varianceHref : null}
            fromPath={currentPath}
            firstMissingCostCenterLinePos={canManageCostCenters ? String(firstMissingCostCenterLine?.line_pos ?? "") : null}
          />

          {standaloneCreditLinks.length > 0 ? (
            <CreditEvidenceSection
              beleg={beleg}
              links={standaloneCreditLinks}
              actionsPending={creditLinkMutation.isPending}
              feedback={creditLinkFeedback}
              fromPath={currentPath}
              onAction={recordCreditLinkAction}
            />
          ) : null}

          {selectedReviewLine ? (
            <InlineArticleHistory
              key={lineReviewKey(selectedReviewLine)}
              line={selectedReviewLine}
              fromPath={currentPath}
              projectByKey={projectByKey}
            />
          ) : null}

          {anomalyLines.length > 0 && selectedReviewLine ? (
            <PriceAnomalyReviewPanel
              beleg={beleg}
              lines={anomalyLines}
              selectedLine={selectedReviewLine}
              selectedLineKey={lineReviewKey(selectedReviewLine)}
              reviewPending={reviewMutation.isPending}
              reviewSuccess={reviewSuccess}
              reviewError={reviewError}
              creditLinks={creditLinks}
              creditActionsPending={creditLinkMutation.isPending}
              creditFeedback={creditLinkFeedback}
              analysisHref={varianceHref}
              onOpenHelp={(returnFocusTarget) =>
                openHelpGuide("price-anomaly-review", returnFocusTarget)
              }
              fromPath={currentPath}
              onSelectLine={selectReviewLine}
              onReview={recordReview}
              onCreditAction={recordCreditLinkAction}
            />
          ) : null}

          <BelegReviewTimeline
            entries={(reviewEvents ?? []).map((event) => ({
              id: event.event_id,
              createdAt: event.created_at,
              action: reviewEventLabel(event),
              status: event.status === "open" ? reviewStatusLabel("open") : event.status === "in_review" ? "In Bearbeitung" : "Abgeschlossen",
              actorId: event.actor_user_id,
              actorLabel: event.actor_label,
              comment: event.comment,
              position: String(event.line_pos),
              note: historicalReviewEvents.some((historical) => historical.event_id === event.event_id) ? "Position aktuell nicht mehr auffällig" : null,
            }))}
          />
        </>
      ) : null}
    </main>
  );
}

function RequestedReviewTargetNotice({
  title,
  message,
}: {
  title: string;
  message: string;
}) {
  return (
    <section
      role="status"
      className="mt-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950"
    >
      <h2 className="font-semibold">{title}</h2>
      <p className="mt-1">{message}</p>
    </section>
  );
}

function focusReviewEditor(attempt = 0) {
  window.requestAnimationFrame(() => {
    const editor = document.getElementById("preispruefung-editor");
    if (!editor) {
      if (attempt < 5) focusReviewEditor(attempt + 1);
      return;
    }
    editor.focus({ preventScroll: true });
    editor.scrollIntoView({ block: "start" });
    if (document.activeElement !== editor && attempt < 5) {
      focusReviewEditor(attempt + 1);
    }
  });
}

function reviewTargetToken(
  belegId: string,
  target: ReturnType<typeof parseBelegReviewUrl>,
): string {
  const linePos = target.kind === "line" ? target.linePos : "";
  return `${belegId}|${target.kind}|${linePos}|${target.returnTo ?? ""}`;
}


function countLabel(
  value: string | number | null | undefined,
  singular: string,
  plural: string,
): string {
  const count = toNumber(value) ?? 0;
  return `${formatQty(value)} ${count === 1 ? singular : plural}`;
}

function BelegStatusChips({
  beleg,
  sourceCounts,
  varianceHref,
  fromPath,
  firstMissingCostCenterLinePos,
}: {
  beleg: BelegRow;
  sourceCounts: Record<string, number>;
  varianceHref: string | null;
  fromPath: string;
  firstMissingCostCenterLinePos: string | null;
}) {
  const missing = toNumber(beleg.missing_cost_center_line_count) ?? 0;
  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-500"
      aria-label="Nachweise zum Beleg"
    >
      <span className="font-medium text-slate-700">Nachweise</span>
      <EvidenceSummaryItem
        active={beleg.pdf_available}
        activeLabel="PDF vorhanden"
        missingLabel="PDF fehlt"
      />
      <EvidenceSummaryItem
        active={sourceCounts.orders > 0}
        activeLabel={`Auftragsabgleich: ${countLabel(sourceCounts.orders, "Auftrag", "Aufträge")}`}
        missingLabel="Auftragsabgleich fehlt"
      />
      <EvidenceSummaryItem
        active={sourceCounts.ugl > 0}
        activeLabel={`UGL: ${countLabel(sourceCounts.ugl, "Position", "Positionen")}`}
        missingLabel="UGL fehlt"
      />
      {missing > 0 ? (
        firstMissingCostCenterLinePos ? (
          <Link to={`${fromPath.split("#")[0]}#kostenstelle-pruefen-${encodeURIComponent(firstMissingCostCenterLinePos)}`} className="inline-flex items-center gap-1.5 text-amber-700 hover:underline">
            <span aria-hidden="true">!</span>
            {countLabel(missing, "offene Zuordnung", "offene Zuordnungen")}
          </Link>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-amber-700"><span aria-hidden="true">!</span>{countLabel(missing, "offene Zuordnung", "offene Zuordnungen")}</span>
        )
      ) : (
        <EvidenceSummaryItem
          active
          activeLabel="Kostenstellen vollständig"
          missingLabel=""
        />
      )}
      {varianceHref ? (
        <Link
          to={varianceHref}
          state={{ from: fromPath }}
          className="ml-auto text-xs font-medium text-emerald-700 hover:underline"
        >
          In der Analyse öffnen →
        </Link>
      ) : null}
    </div>
  );
}

type CreditLinkFeedback = {
  linkId: string;
  tone: "pending" | "success" | "error";
  message: string;
};

function CreditEvidenceSection({
  beleg,
  links,
  actionsPending,
  feedback,
  fromPath,
  onAction,
  embedded = false,
}: {
  beleg: BelegRow;
  links: CreditAnomalyLink[];
  actionsPending: boolean;
  feedback: CreditLinkFeedback | null;
  fromPath: string;
  onAction: (
    link: CreditAnomalyLink,
    action: "confirmed" | "revoked",
  ) => void;
  embedded?: boolean;
}) {
  const showingCredit = beleg.doc_type === "gutschrift";
  const title = showingCredit
    ? "Zuordnung zu Ursprungsrechnungen"
    : embedded
      ? "Gutschrift zu dieser Position"
      : "Weitere Gutschrift-Zuordnungen";
  const description = showingCredit
    ? "Zeigt, welchen Preisprüfungen diese Gutschrift zugeordnet ist."
    : embedded
      ? "Eingegangene Gutschrift prüfen und dieser Preisauffälligkeit zuordnen."
      : "Zuordnungen zu früheren oder nicht mehr aktiven Preisprüfungen.";
  return (
    <section
      id={
        embedded || showingCredit
          ? "gutschrift-zuordnungen"
          : "weitere-gutschrift-zuordnungen"
      }
      className={
        embedded
          ? "mt-4 scroll-mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4"
          : "mt-6 scroll-mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
      }
    >
      {embedded ? (
        <h4 className="text-base font-semibold text-slate-900">{title}</h4>
      ) : (
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      )}
      <p className="mt-1 text-sm text-slate-500">{description}</p>

      <div className="mt-4 space-y-3">
        {links.map((link) => {
          const counterpartId = showingCredit
            ? link.invoice_doc_id
            : link.credit_doc_id;
          const counterpartNumber = showingCredit
            ? link.invoice_doc_number
            : link.credit_doc_number;
          const counterpartDate = showingCredit
            ? link.invoice_doc_date
            : link.credit_doc_date;
          const counterpartLine = showingCredit
            ? link.invoice_line_pos
            : link.credit_line_pos;
          const counterpartPdfAvailable = showingCredit
            ? link.invoice_pdf_available
            : link.credit_pdf_available;
          const counterpartKind = showingCredit
            ? "Ursprungsrechnung"
            : "Gutschrift";
          const counterpartContext = `${counterpartKind} ${
            counterpartNumber ?? counterpartId
          }, Position ${counterpartLine}`;
          const confirmableAmount =
            toNumber(link.confirmable_amount) ??
            toNumber(link.allocation_amount) ??
            0;
          const proposedAmount = toNumber(link.allocation_amount) ?? 0;
          const partialConfirmation =
            link.state === "proposed" && confirmableAmount < proposedAmount;
          const canRestore =
            link.state === "revoked" && !link.is_superseded;
          const linkFeedback =
            feedback?.linkId === link.link_id ? feedback : null;
          const referenceEvidence = creditReferenceEvidence(link);
          const explanation = creditMatchExplanation(link);
          return (
            <article
              key={link.link_id}
              id={creditLinkAnchorId(link)}
              className={
                embedded
                  ? "rounded-lg border border-slate-200 bg-white p-4"
                  : "rounded-lg border border-slate-200 bg-slate-50 p-4"
              }
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <Link
                      to={`/belege/${encodeURIComponent(
                        counterpartId,
                      )}#${encodeURIComponent(creditLinkAnchorId(link))}`}
                      state={{ from: fromPath }}
                      aria-label={counterpartContext}
                      className="text-base font-semibold text-emerald-700 hover:underline"
                    >
                      {counterpartKind} {" "}
                      {counterpartNumber ?? counterpartId}
                    </Link>
                    <span className="text-slate-500">
                      Position {counterpartLine}
                    </span>
                    <span className="text-slate-400">
                      {formatDate(counterpartDate)}
                    </span>
                    {counterpartPdfAvailable ? (
                      <DocumentPdfActions
                        docId={counterpartId}
                        docNumber={counterpartNumber ?? counterpartId}
                        linePos={counterpartLine}
                        label="PDF"
                        className="text-xs"
                      />
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <CreditLinkStateBadge
                    link={link}
                    creditPerspective={showingCredit}
                  />
                  {!isDemoMode() ? (
                    <>
                      {link.state === "proposed" || canRestore ? (
                        <button
                          type="button"
                          disabled={actionsPending || confirmableAmount <= 0}
                          onClick={() => onAction(link, "confirmed")}
                          aria-label={`${
                            link.state === "revoked"
                              ? "Erneut bestätigen"
                              : "Bestätigen"
                          }: ${counterpartContext}`}
                          className="h-9 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                        >
                          {link.state === "revoked"
                            ? "Erneut bestätigen"
                            : "Bestätigen"}
                        </button>
                      ) : null}
                      {link.state !== "revoked" ? (
                        <button
                          type="button"
                          disabled={actionsPending}
                          onClick={() => onAction(link, "revoked")}
                          aria-label={`${
                            link.state === "proposed"
                              ? "Vorschlag verwerfen"
                              : "Zuordnung aufheben"
                          }: ${counterpartContext}`}
                          className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:text-slate-300"
                        >
                          {link.state === "proposed"
                            ? "Vorschlag verwerfen"
                            : "Zuordnung aufheben"}
                        </button>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>

              {link.state === "proposed" ? (
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  {creditProposalPrompt(link, showingCredit)}
                </p>
              ) : null}

              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-slate-200 pt-4 sm:grid-cols-4">
                <ReviewMetric
                  label="Gutschriftbetrag"
                  value={formatEuro(link.credit_source_amount)}
                />
                <ReviewMetric
                  label={
                    link.state === "proposed"
                      ? "Vorgeschlagen"
                      : link.state === "confirmed"
                        ? "Dieser Prüfung zugeordnet"
                        : link.is_superseded
                          ? "Ersetzter Vorschlag"
                          : "Aufgehobene Zuordnung"
                  }
                  value={formatEuro(
                    link.state === "proposed"
                      ? link.confirmable_amount
                      : link.allocation_amount,
                  )}
                />
                <ReviewMetric
                  label={showingCredit ? "Noch verfügbar" : "Erwartet"}
                  value={formatEuro(
                    showingCredit
                      ? link.credit_remaining_amount
                      : link.expected_amount,
                  )}
                />
                <ReviewMetric
                  label={showingCredit ? "Rest der Erwartung" : "Rest offen"}
                  value={formatEuro(link.remaining_amount)}
                  tone={
                    (toNumber(link.remaining_amount) ?? 0) > 0 ? "rose" : "slate"
                  }
                />
              </dl>

              {partialConfirmation ? (
                <p className="mt-2 text-xs text-amber-700">
                  Beim Bestätigen wird der Vorschlag automatisch auf den noch
                  verfügbaren Betrag begrenzt.
                </p>
              ) : null}

              {linkFeedback ? (
                <p
                  role={linkFeedback.tone === "error" ? "alert" : "status"}
                  aria-live={
                    linkFeedback.tone === "error" ? "assertive" : "polite"
                  }
                  aria-atomic="true"
                  className={
                    linkFeedback.tone === "error"
                      ? "mt-3 text-sm font-medium text-rose-700"
                      : linkFeedback.tone === "success"
                        ? "mt-3 text-sm font-medium text-emerald-700"
                        : "mt-3 text-sm text-slate-500"
                  }
                >
                  {linkFeedback.message}
                </p>
              ) : null}

              <details className="mt-4 border-t border-slate-200 pt-3 text-sm text-slate-600">
                <summary className="cursor-pointer font-medium text-emerald-700 hover:underline">
                  {link.state === "proposed"
                    ? "Warum vorgeschlagen?"
                    : "Nachweis der Zuordnung"}
                </summary>
                <div className="mt-3 space-y-2 leading-relaxed">
                  <p>{explanation}</p>
                  {referenceEvidence ? (
                    <p>
                      <span className="font-medium text-slate-700">
                        Quellnachweis:
                      </span>{" "}
                      {referenceEvidence}
                    </p>
                  ) : null}
                  {link.latest_reason ? (
                    <p>
                      <span className="font-medium text-slate-700">
                        Letzte Änderung:
                      </span>{" "}
                      {link.latest_reason}
                    </p>
                  ) : null}
                </div>
              </details>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function CreditLinkStateBadge({
  link,
  creditPerspective,
}: {
  link: CreditAnomalyLink;
  creditPerspective: boolean;
}) {
  if (link.state === "revoked") {
    if (link.is_superseded) {
      return (
        <span className="inline-flex items-center rounded-md bg-slate-200 px-2.5 py-1 text-sm font-semibold text-slate-700">
          Ersetzt
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-md bg-slate-200 px-2.5 py-1 text-sm font-semibold text-slate-700">
        Aufgehoben
      </span>
    );
  }
  if (link.state === "proposed") {
    return (
      <span className="inline-flex items-center rounded-md bg-amber-100 px-2.5 py-1 text-sm font-semibold text-amber-800">
        Zuordnung prüfen
      </span>
    );
  }
  if ((toNumber(link.remaining_amount) ?? 0) > 0.01) {
    return (
      <span className="inline-flex items-center rounded-md bg-sky-100 px-2.5 py-1 text-sm font-semibold text-sky-800">
        Teilgutschrift
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-emerald-100 px-2.5 py-1 text-sm font-semibold text-emerald-800">
      <span aria-hidden="true">✓&nbsp;</span>
      {creditPerspective
        ? "Preisprüfung vollständig gedeckt"
        : "Vollständig gedeckt"}
    </span>
  );
}

function creditMatchExplanation(link: CreditAnomalyLink): string {
  if (link.confidence_class === "strong_explicit") {
    return "Rechnungsreferenz und Artikel stimmen eindeutig überein.";
  }
  if (link.match_rule === "explicit_predecessor_position_candidate") {
    return "Die Gutschrift enthält eine eindeutige Rechnungsreferenz. Weil der Artikelcode nicht eindeutig übereinstimmt, muss die Position fachlich bestätigt werden.";
  }
  if (link.match_rule === "explicit_predecessor_exact_article") {
    return "Die Gutschrift enthält eine eindeutige Rechnungsreferenz und einen passenden Artikel. Mehrere mögliche Rechnungspositionen verhindern eine automatische Zuordnung.";
  }
  return "Artikel, Großhändler und Zeitraum stimmen überein. Ohne eindeutige Rechnungsreferenz bleibt die Zuordnung bestätigungspflichtig.";
}

function creditProposalPrompt(
  link: CreditAnomalyLink,
  showingCredit: boolean,
): string {
  if (showingCredit) {
    return `Bitte prüfen, ob Rechnung ${
      link.invoice_doc_number ?? link.invoice_doc_id
    }, Position ${link.invoice_line_pos}, gemeint ist.`;
  }
  return `Die Gutschrift verweist auf diese Rechnung. Bitte prüfen, ob Position ${link.invoice_line_pos} gemeint ist.`;
}

function creditLinkAnchorId(link: CreditAnomalyLink): string {
  return `gutschrift-zuordnung-${link.link_id}`;
}

function locationHashTarget(hash: string): string {
  const rawTarget = hash.startsWith("#") ? hash.slice(1) : hash;
  try {
    return decodeURIComponent(rawTarget);
  } catch {
    return rawTarget;
  }
}

function creditReferenceEvidence(link: CreditAnomalyLink): string | null {
  const explicit = link.source_references.explicit;
  if (!Array.isArray(explicit)) {
    return null;
  }
  const labels = explicit.flatMap((reference) => {
    if (
      typeof reference !== "object" ||
      reference === null ||
      Array.isArray(reference)
    ) {
      return [];
    }
    const sourceLabel =
      typeof reference.source_label === "string"
        ? reference.source_label.trim()
        : "";
    if (!sourceLabel) {
      return [];
    }
    const rawValue =
      typeof reference.ref_value_raw === "string"
        ? reference.ref_value_raw.trim()
        : "";
    return [rawValue ? `${sourceLabel} · ${rawValue}` : sourceLabel];
  });
  const uniqueLabels = [...new Set(labels)];
  return uniqueLabels.length > 0 ? uniqueLabels.join(", ") : null;
}

function ReviewMetric({
  label,
  value,
  tone = "slate",
}: {
  label: string;
  value: string;
  tone?: "slate" | "rose";
}) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd
        className={
          tone === "rose"
            ? "mt-1 text-base font-semibold tabular-nums text-rose-700"
            : "mt-1 text-base font-semibold tabular-nums text-slate-900"
        }
      >
        {value}
      </dd>
    </div>
  );
}

function EvidenceSummaryItem({
  active,
  activeLabel,
  missingLabel,
}: {
  active: boolean;
  activeLabel: string;
  missingLabel: string;
}) {
  if (active) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span aria-hidden="true" className="text-emerald-600">
          ✓
        </span>
        {activeLabel}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-slate-400">
      <span aria-hidden="true">•</span>
      {missingLabel}
    </span>
  );
}

function BelegPositionsSection({
  rows,
  docNumber,
  fromPath,
  projectByKey,
  selectedLineKey,
  reviewSelectionDisabled,
  onSelectReview,
  reviewableCostCenter,
  costCenterDecisions,
}: {
  rows: BelegLineItem[];
  docNumber: string;
  fromPath: string;
  projectByKey: ReadonlyMap<string, ProjectSummary>;
  selectedLineKey: string | null;
  reviewSelectionDisabled: boolean;
  onSelectReview: (lineKey: string) => void;
  reviewableCostCenter: boolean;
  costCenterDecisions: Record<string, "assigned" | "deferred" | "open">;
}) {
  const table = (
    <BelegLinesTable
      rows={rows}
      docNumber={docNumber}
      fromPath={fromPath}
      projectByKey={projectByKey}
      selectedLineKey={selectedLineKey}
      reviewSelectionDisabled={reviewSelectionDisabled}
      showReviewHint
      onSelectReview={onSelectReview}
      reviewableCostCenter={reviewableCostCenter}
      costCenterDecisions={costCenterDecisions}
    />
  );
  const note =
    "Belegwerte kommen direkt aus den Positionen; Kostenstellen-Zuordnungen erhöhen diese Summe nicht.";

  return (
    <section id="belegpositionen" className="mt-8 scroll-mt-6">
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Positionen
        </h2>
        <span className="text-xs text-slate-400">{note}</span>
      </div>
      {table}
    </section>
  );
}

function BelegLinesTable({
  rows,
  docNumber,
  fromPath,
  projectByKey,
  selectedLineKey,
  reviewSelectionDisabled,
  showReviewHint = true,
  onSelectReview,
  reviewableCostCenter,
  costCenterDecisions,
}: {
  rows: BelegLineItem[];
  docNumber: string;
  fromPath: string;
  projectByKey: ReadonlyMap<string, ProjectSummary>;
  selectedLineKey: string | null;
  reviewSelectionDisabled: boolean;
  showReviewHint?: boolean;
  onSelectReview: (lineKey: string) => void;
  reviewableCostCenter: boolean;
  costCenterDecisions: Record<string, "assigned" | "deferred" | "open">;
}) {
  const assignments = useAssignmentWorklist({doc_id: rows[0]?.beleg_id ?? "", status:"all",limit:"1000"});
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-6 text-sm text-slate-500">
        Keine Positionen gefunden.
      </div>
    );
  }
  const hasPriceAnomalies = rows.some((row) => row.is_price_anomaly);

  function selectReviewRow(row: BelegLineItem): void {
    if (!row.is_price_anomaly || reviewSelectionDisabled) return;
    onSelectReview(lineReviewKey(row));
  }

  function handleRowClick(
    row: BelegLineItem,
    event: MouseEvent<HTMLTableRowElement>,
  ): void {
    if (!row.is_price_anomaly || isInteractiveTarget(event.target)) return;
    selectReviewRow(row);
  }

  function handleRowKeyDown(
    row: BelegLineItem,
    event: KeyboardEvent<HTMLTableRowElement>,
  ): void {
    if (!row.is_price_anomaly || isInteractiveTarget(event.target)) return;
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    selectReviewRow(row);
  }

  return (
    <>
      {showReviewHint && hasPriceAnomalies ? (
        <p className="mb-2 text-xs text-slate-500">
          Auffällige Position auswählen, um die Prüfung zu bearbeiten.
        </p>
      ) : null}
      <div className="max-w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm [contain:layout_paint]">
        <div className="divide-y divide-slate-100 md:hidden">
          {rows.map((row, index) => {
            const rowKey = lineReviewKey(row);
            const selectable = row.is_price_anomaly && !reviewSelectionDisabled;
            return (
              <article
                key={`${row.beleg_id}-${row.line_pos ?? index}`}
                className={`p-3 ${selectedLineKey === rowKey ? "bg-emerald-50/60" : row.is_price_anomaly ? "bg-rose-50/30" : ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-mono text-xs text-slate-500">Position {row.line_pos ?? index + 1}</span>
                  <PriceReviewCell row={row} />
                </div>
                <div className="mt-2 min-w-0">
                  <ArticleCell row={row} fromPath={fromPath} />
                  {row.line_pos != null ? <ArticleAssignment docId={row.beleg_id} linePos={String(row.line_pos)} review={assignments.isFetching ? undefined : assignments.data?.items.find(item => item.line_pos === String(row.line_pos))} /> : null}
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div className="min-w-0"><dt className="text-slate-500">Kostenstelle</dt><dd className="mt-0.5 break-words font-medium"><CostCenterCell row={row} fromPath={fromPath} projectByKey={projectByKey} reviewableCostCenter={reviewableCostCenter} manualDecisionStatus={costCenterDecisions[String(row.line_pos)]} /></dd></div>
                  <div className="min-w-0 text-right"><dt className="text-slate-500">Menge</dt><dd className="mt-0.5 tabular-nums">{formatQty(row.qty)} {row.uom ?? ""}</dd></div>
                  <div className="min-w-0"><dt className="text-slate-500">Einzelpreis</dt><dd className="mt-0.5 tabular-nums">{formatEuro(row.effective_unit_price ?? row.net_unit_price)}</dd></div>
                  <div className="min-w-0 text-right"><dt className="text-slate-500">Betrag</dt><dd className="mt-0.5 tabular-nums font-medium">{formatEuro(row.signed_line_amount)}</dd></div>
                  <div className="col-span-2"><dt className="text-slate-500">Abweichung</dt><dd className="mt-0.5"><PriceDeviationCell row={row} /></dd></div>
                </dl>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-2">
                  {row.pdf_available ? <DocumentPdfActions docId={row.beleg_id} docNumber={docNumber} label="PDF" sourceKind={row.source_kind} linePos={row.line_pos} pageNo={toNumber(row.page_no)} /> : <span className="text-xs text-slate-400">Kein PDF</span>}
                  {row.is_price_anomaly ? <button
                    type="button"
                    disabled={!selectable}
                    onClick={() => selectReviewRow(row)}
                    aria-label={`Preisprüfung Position ${row.line_pos ?? index + 1} auswählen`}
                    className="h-9 rounded-md border border-slate-300 bg-white px-3 text-xs font-medium text-emerald-700 disabled:opacity-50"
                  >Preisprüfung öffnen →</button> : null}
                </div>
              </article>
            );
          })}
        </div>
        <div className="hidden max-w-full overflow-x-auto md:block">
          <table className="w-full min-w-[1200px] table-fixed text-sm">
            <colgroup>
              <col className="w-[6%]" />
              <col className="w-[24%]" />
              <col className="w-[9%]" />
              <col className="w-[17%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
              <col className="w-[8%]" />
              <col className="w-[9%]" />
              <col className="w-[11%]" />
            </colgroup>
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <Th>Zeile</Th>
                <Th>Artikel</Th>
                <Th>Nachweis</Th>
                <Th>Kostenstelle</Th>
                <Th align="right">Menge</Th>
                <Th align="right">Einzelpreis</Th>
                <Th align="right">Betrag</Th>
                <Th align="right">Abweichung</Th>
                <Th>Prüfung</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((row, index) => {
                const rowKey = lineReviewKey(row);
                const selected = selectedLineKey === rowKey;
                const selectable = row.is_price_anomaly && !reviewSelectionDisabled;
                const rowClassName = selected
                  ? `${selectable ? "cursor-pointer" : "cursor-wait"} bg-emerald-50/60 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-emerald-400`
                  : selectable
                    ? "cursor-pointer bg-rose-50/30 outline-none transition-colors hover:bg-rose-50/60 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rose-300"
                    : "hover:bg-slate-50";
                return (
                  <tr
                    key={`${row.beleg_id}-${row.line_pos ?? index}`}
                    className={rowClassName}
                    tabIndex={selectable ? 0 : undefined}
                    aria-disabled={
                      row.is_price_anomaly ? reviewSelectionDisabled : undefined
                    }
                    aria-label={
                      row.is_price_anomaly
                        ? `Preisprüfung Position ${row.line_pos ?? index + 1} auswählen`
                        : undefined
                    }
                    onClick={(event) => handleRowClick(row, event)}
                    onKeyDown={(event) => handleRowKeyDown(row, event)}
                  >
                    <td className="px-3 py-2 font-mono text-xs text-slate-500">
                      {row.line_pos ?? "–"}
                    </td>
                    <td className="px-3 py-2">
                      <ArticleCell row={row} fromPath={fromPath} />
                      {row.line_pos != null ? <ArticleAssignment docId={row.beleg_id} linePos={String(row.line_pos)} review={assignments.isFetching ? undefined : assignments.data?.items.find(item => item.line_pos === String(row.line_pos))} /> : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.pdf_available ? (
                        <DocumentPdfActions
                          docId={row.beleg_id}
                          docNumber={docNumber}
                          label="PDF"
                          sourceKind={row.source_kind}
                          linePos={row.line_pos}
                          pageNo={toNumber(row.page_no)}
                        />
                      ) : (
                        <span className="text-slate-400">–</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <CostCenterCell
                        row={row}
                        fromPath={fromPath}
                        projectByKey={projectByKey}
                        reviewableCostCenter={reviewableCostCenter}
                        manualDecisionStatus={costCenterDecisions[String(row.line_pos)]}
                      />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                      {formatQty(row.qty)}
                      {row.uom ? (
                        <span className="ml-1 text-xs text-slate-400">
                          {row.uom}
                        </span>
                      ) : null}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-slate-700">
                      {formatEuro(row.effective_unit_price ?? row.net_unit_price)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums font-medium text-slate-900">
                      {formatEuro(row.signed_line_amount)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                      <PriceDeviationCell row={row} />
                    </td>
                    <td className="px-3 py-2">
                      <PriceReviewCell row={row} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    Boolean(
      target.closest(
        'a, button, input, select, textarea, summary, [role="button"], [role="link"]',
      ),
    )
  );
}

function ArticleCell({
  row,
  fromPath,
}: {
  row: BelegLineItem;
  fromPath: string;
}) {
  const label = row.primary_article_code ?? row.article_group_key;
  const description = row.description
    ? (cleanDescription(row.description) ?? row.description)
    : null;
  const articleHref =
    row.article_group_key && row.article_detail_available
      ? `/articles/${encodeURIComponent(row.article_group_key)}`
      : null;
  if (!label) {
    return (
      <span className="block break-words text-slate-700 md:truncate">
        {description ?? "–"}
      </span>
    );
  }
  return (
    <div className="min-w-0">
      {articleHref ? (
        <Link
          to={articleHref}
          state={{ from: fromPath }}
          className="inline-block max-w-full break-all align-bottom font-mono text-xs font-medium text-emerald-700 hover:underline md:truncate"
        >
          {label}
        </Link>
      ) : (
        <span className="block break-all font-mono text-xs font-medium text-slate-800 md:truncate">
          {label}
        </span>
      )}
      {description ? (
        <span className="mt-0.5 block break-words text-slate-600 md:truncate" title={description}>
          {description}
        </span>
      ) : null}
    </div>
  );
}

function CostCenterCell({
  row,
  fromPath,
  projectByKey,
  reviewableCostCenter = false,
  manualDecisionStatus,
}: {
  row: BelegLineItem;
  fromPath: string;
  projectByKey: ReadonlyMap<string, ProjectSummary>;
  reviewableCostCenter?: boolean;
  manualDecisionStatus?: "assigned" | "deferred" | "open";
}) {
  const location = useLocation();
  const projectHref = projectHrefFromKey(row.project_key);
  const deliveryHref = deliveryAddressCostCenterHref(row.delivery_address_key);
  const referenceHref = customerReferenceHrefFromKey(row.customer_reference);
  const editDecisionHref = reviewableCostCenter && manualDecisionStatus != null && row.line_pos != null
    ? `${fromPath.split("#")[0]}#kostenstelle-pruefen-${encodeURIComponent(String(row.line_pos))}`
    : null;
  const editDecisionLink = editDecisionHref ? <Link to={editDecisionHref} state={location.state} className="mt-0.5 block text-xs font-normal text-emerald-700 hover:underline">{manualDecisionStatus === "assigned" ? "Zuordnung ändern" : "Entscheidung ansehen"}</Link> : null;
  if (!row.has_cost_center_context) {
    const reviewHref = reviewableCostCenter && row.line_pos != null
      ? `${fromPath.split("#")[0]}#kostenstelle-pruefen-${encodeURIComponent(String(row.line_pos))}`
      : null;
    return (
      <div className="min-w-0">
        {reviewHref ? <Link to={reviewHref} state={location.state} className="block text-amber-700 hover:underline">Zuordnung offen →</Link>
          : <span className="block text-amber-700">Zuordnung offen</span>}
        {deliveryHref ? (
          <Link
            to={deliveryHref}
            state={{ from: fromPath }}
            className="mt-0.5 block max-w-full break-words text-xs font-normal text-slate-600 hover:underline md:truncate"
            title={row.delivery_address_display ?? row.delivery_address_key ?? undefined}
          >
            Lieferadresse: {row.delivery_address_display ?? row.delivery_address_key}
          </Link>
        ) : null}
        {row.customer_reference ? (
          <span className="mt-0.5 block max-w-full break-words text-xs font-normal text-slate-600 md:truncate" title={row.customer_reference}>
            Belegreferenz: {row.customer_reference}
          </span>
        ) : null}
      </div>
    );
  }
  if (projectHref) {
    const contextType = costCenterTypeFromProjectKey(row.project_key);
    const display = contextType === "project" ? projectDisplay(
      projectByKey.get(row.project_key ?? ""), row.project_key,
    ) : { title: displayCostCenterKey(contextType, row.project_key ?? ""), tooltip: undefined };
    return (
      <div className="min-w-0"><Link
        to={projectHref}
        state={{ from: fromPath }}
        className="inline-block max-w-full break-words align-bottom text-emerald-700 hover:underline md:truncate"
        title={display?.tooltip}
      >
        {display?.title ?? projectLabelFromKey(row.project_key)}
      </Link>{manualDecisionStatus === "assigned" ? <span className="block text-xs font-normal text-slate-500">Manuell zugeordnet</span> : null}{editDecisionLink}</div>
    );
  }
  if (deliveryHref) {
    return (
      <div className="min-w-0"><Link
        to={deliveryHref}
        state={{ from: fromPath }}
        className="inline-block max-w-full break-words align-bottom text-emerald-700 hover:underline md:truncate"
      >
        {row.delivery_address_display ?? row.delivery_address_key}
      </Link>{manualDecisionStatus === "assigned" ? <span className="block text-xs font-normal text-slate-500">Manuell zugeordnet</span> : null}{editDecisionLink}</div>
    );
  }
  if (referenceHref) {
    return (
      <div className="min-w-0"><Link
        to={referenceHref}
        state={{ from: fromPath }}
        className="inline-block max-w-full break-words align-bottom text-emerald-700 hover:underline md:truncate"
      >
        {displayCostCenterKey("customer_reference", row.customer_reference ?? "")}
      </Link>{manualDecisionStatus === "assigned" ? <span className="block text-xs font-normal text-slate-500">Manuell zugeordnet</span> : null}{editDecisionLink}</div>
    );
  }
  if (row.customer_reference) {
    return (
      <span className="block truncate text-slate-700" title={row.customer_reference}>
        {row.customer_reference}
      </span>
    );
  }
  return (
    <span className={row.has_cost_center_context ? "text-slate-700" : "text-amber-700"}>
      {row.has_cost_center_context ? "vorhanden" : "fehlt"}
    </span>
  );
}

/**
 * Abweichung zum Vergleichspreis für jede Position, die einen hat – nicht nur
 * für Preisauffälligkeiten. Farben wie überall (#563): rot teurer, grün
 * günstiger; fett, wenn die Prüfregel greift.
 */
function PriceDeviationCell({ row }: { row: BelegLineItem }) {
  const delta = toNumber(row.canonical_delta_pct);
  if (delta === null) {
    return <span className="text-slate-300" title="Kein früherer Preis zum Vergleich">–</span>;
  }
  const tone = delta > 0 ? "text-rose-700" : delta < 0 ? "text-emerald-700" : "text-slate-600";
  return (
    <span
      className={`${tone} ${row.is_price_anomaly ? "font-semibold" : ""}`}
      title={`${baselineKindLabel(row.canonical_baseline_kind)}: ${formatEuro(row.canonical_baseline_price)}`}
    >
      {formatSignedPercent(row.canonical_delta_pct)}
    </span>
  );
}

function PriceReviewCell({ row }: { row: BelegLineItem }) {
  if (!row.is_price_anomaly) {
    return <span className="sr-only">Keine Preisprüfung</span>;
  }
  return (
    <div className="flex items-start text-xs">
      <LineReviewStatusBadge
        value={row.price_anomaly_status ?? "open"}
        action={row.price_anomaly_action}
      />
    </div>
  );
}

function PriceAnomalyReviewPanel({
  beleg,
  lines,
  selectedLine,
  selectedLineKey,
  reviewPending,
  reviewSuccess,
  reviewError,
  creditLinks,
  creditActionsPending,
  creditFeedback,
  analysisHref,
  onOpenHelp,
  fromPath,
  onSelectLine,
  onReview,
  onCreditAction,
}: {
  beleg: BelegRow;
  lines: BelegLineItem[];
  selectedLine: BelegLineItem;
  selectedLineKey: string;
  reviewPending: boolean;
  reviewSuccess: ReviewSuccess | null;
  reviewError: ReviewError | null;
  creditLinks: CreditAnomalyLink[];
  creditActionsPending: boolean;
  creditFeedback: CreditLinkFeedback | null;
  analysisHref: string | null;
  onOpenHelp: (returnFocusTarget: HTMLElement | null) => void;
  fromPath: string;
  onSelectLine: (lineKey: string) => void;
  onReview: (
    row: BelegLineItem,
    action: BelegPriceAnomalyAction,
    comment: string,
  ) => void;
  onCreditAction: (
    link: CreditAnomalyLink,
    action: "confirmed" | "revoked",
  ) => void;
}) {
  const errorMessage =
    reviewError?.lineKey === selectedLineKey ? reviewError.message : null;
  const hasOtherOpenLine = lines.some(
    (line) =>
      lineReviewKey(line) !== selectedLineKey &&
      (line.price_anomaly_status ?? "open") === "open",
  );
  const selectedDescription =
    cleanDescription(selectedLine.description ?? "") ?? selectedLine.description;
  const selectedCreditLinks = creditLinks.filter(
    (link) =>
      String(link.invoice_line_pos) === String(selectedLine.line_pos ?? ""),
  );
  const selectedIndex = Math.max(
    0,
    lines.findIndex((line) => lineReviewKey(line) === selectedLineKey),
  );
  const previousLine =
    lines.length > 1
      ? lines[(selectedIndex - 1 + lines.length) % lines.length]
      : null;
  const nextLine =
    lines.length > 1 ? lines[(selectedIndex + 1) % lines.length] : null;

  return (
    <section
      id="preispruefung-editor"
      aria-labelledby="preispruefung-heading"
      tabIndex={-1}
      className="mt-6 scroll-mt-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 sm:p-5"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5">
            <h2
            id="preispruefung-heading"
            className="text-base font-semibold text-slate-900"
          >
            Preisprüfung {selectedIndex + 1} von {lines.length}
          </h2>
            <SectionInfo label="Preisprüfung">
              Preis mit früheren Rechnungen vergleichen und die Entscheidung nachvollziehbar dokumentieren.
            </SectionInfo>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
          {analysisHref ? (
            <Link
              to={analysisHref}
              state={{ from: fromPath }}
              className="font-medium text-emerald-700 hover:underline"
            >
              In der Analyse öffnen →
            </Link>
          ) : null}
        </div>
      </div>

      <div className="mt-3 text-sm text-slate-600">
        <button
          type="button"
          onClick={(event) => onOpenHelp(event.currentTarget)}
          className="text-xs font-medium text-emerald-700 hover:underline"
        >
          So funktioniert die Preisprüfung →
        </button>
      </div>

      <div className="mt-5 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-800">
            Position {selectedLine.line_pos ?? "–"}
          </h3>
        </div>
        {previousLine && nextLine ? (
          <div
            className="flex items-center gap-1.5 self-start sm:shrink-0 sm:self-auto"
            aria-label="Preisprüfungen wechseln"
          >
            <button
              type="button"
              disabled={reviewPending}
              onClick={() => onSelectLine(lineReviewKey(previousLine))}
              className="h-9 rounded-md border border-slate-200 bg-transparent px-2.5 text-xs font-medium text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 disabled:cursor-wait disabled:opacity-60"
              aria-label="Vorherige Preisprüfung"
            >
              ← Zurück
            </button>
            <button
              type="button"
              disabled={reviewPending}
              onClick={() => onSelectLine(lineReviewKey(nextLine))}
              className="h-9 rounded-md border border-slate-200 bg-transparent px-2.5 text-xs font-medium text-slate-500 hover:border-slate-300 hover:bg-slate-50 hover:text-slate-700 disabled:cursor-wait disabled:opacity-60"
              aria-label="Nächste Preisprüfung"
            >
              Weiter →
            </button>
          </div>
        ) : null}
      </div>

      {selectedCreditLinks.length > 0 ? (
        <CreditEvidenceSection
          beleg={beleg}
          links={selectedCreditLinks}
          actionsPending={creditActionsPending}
          feedback={creditFeedback}
          fromPath={fromPath}
          onAction={onCreditAction}
          embedded
        />
      ) : null}

      {reviewSuccess ? (
        <p
          role="status"
          className="mt-3 text-sm font-medium text-emerald-700"
        >
          <span aria-hidden="true">✓ </span>
          {reviewSuccessMessage(reviewSuccess)}
        </p>
      ) : null}

      <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,27rem)]">
          <div className="min-w-0 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-mono text-sm font-semibold text-slate-900">
                  {selectedLine.primary_article_code ??
                    selectedLine.article_group_key ??
                    "Artikel ohne Nummer"}
                </div>
                {selectedDescription ? (
                  <div
                    className="mt-1 line-clamp-2 text-sm text-slate-600"
                    title={selectedDescription}
                  >
                    {selectedDescription}
                  </div>
                ) : null}
              </div>
              <LineReviewStatusBadge
                value={selectedLine.price_anomaly_status ?? "open"}
                action={selectedLine.price_anomaly_action}
                size="prominent"
              />
            </div>

            <PriceComparison line={selectedLine} />
          </div>

          <div className="border-t border-slate-200 p-4 sm:p-5 lg:border-l lg:border-t-0">
            {isDemoMode() ? (
              <div className="text-sm text-slate-600">
                Demo ist schreibgeschützt.
              </div>
            ) : (
              <PriceAnomalyReviewEditor
                row={selectedLine}
                pending={reviewPending}
                errorMessage={errorMessage}
                hasOtherOpenLine={hasOtherOpenLine}
                onReview={onReview}
              />
            )}
          </div>
        </div>
      </div>

    </section>
  );
}

function PriceComparison({ line }: { line: BelegLineItem }) {
  return (
    <div className="mt-5 rounded-md bg-slate-50 px-4 py-3">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <dt className="text-xs text-slate-500">Aktueller Netto-Preis</dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
            {formatEuro(line.effective_unit_price ?? line.net_unit_price)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-slate-500">
            Referenz · {baselineKindLabel(line.canonical_baseline_kind)}
          </dt>
          <dd className="mt-0.5 text-lg font-semibold tabular-nums text-slate-900">
            {formatEuro(line.canonical_baseline_price)}
          </dd>
        </div>
      </dl>
      <p className="mt-3 border-t border-slate-200 pt-3 text-sm">
        <span className="font-semibold tabular-nums text-rose-700">
          {formatSignedPercent(line.canonical_delta_pct)}
        </span>
        <span className="mx-2 text-slate-300">·</span>
        <span className="font-medium tabular-nums text-rose-700">
          {formatReviewOverpay(line.overpay_abs)} Mehrkosten
        </span>
      </p>
    </div>
  );
}

function PriceAnomalyReviewEditor({
  row,
  pending,
  errorMessage,
  hasOtherOpenLine,
  onReview,
}: {
  row: BelegLineItem;
  pending: boolean;
  errorMessage: string | null;
  hasOtherOpenLine: boolean;
  onReview: (
    row: BelegLineItem,
    action: BelegPriceAnomalyAction,
    comment: string,
  ) => void;
}) {
  const [action, setAction] = useState<ReviewNextAction | null>(null);
  const [comment, setComment] = useState("");
  const [copied, setCopied] = useState(false);
  const [complaintTextOpen, setComplaintTextOpen] = useState(false);
  const complaintText = useMemo(() => complaintTextForLine(row), [row]);
  const nextActions = availableReviewActionsForLine(row);

  useEffect(() => {
    setAction(null);
    setComment("");
    setCopied(false);
    setComplaintTextOpen(false);
  }, [
    row.beleg_id,
    row.line_pos,
    row.price_anomaly_action,
    row.price_anomaly_comment,
    row.price_anomaly_reviewed_at,
    row.price_anomaly_status,
  ]);

  const hasLinePos = row.line_pos !== null && row.line_pos !== undefined;
  const trimmedComment = comment.trim();
  const commentRequired = action === "comment" || action === "write_off";
  const missingRequiredComment = commentRequired && trimmedComment.length === 0;
  const canSubmit =
    hasLinePos && action !== null && !missingRequiredComment && !pending;
  const advancesAfterSave =
    action !== null && isAdvancingReviewAction(action) && hasOtherOpenLine;
  function submitReview() {
    if (!canSubmit || action === null) return;
    onReview(row, action, comment);
  }
  async function copyComplaintText() {
    if (!navigator.clipboard) return;
    try {
      await navigator.clipboard.writeText(complaintText);
    } catch {
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  return (
    <form
      className="flex h-full flex-col"
      onSubmit={(event) => {
        event.preventDefault();
        submitReview();
      }}
    >
      <div>
        <h3 className="text-sm font-semibold text-slate-900">Entscheidung</h3>
        <p className="mt-1 text-xs leading-relaxed text-slate-500">
          Wählen Sie, wie diese Preisabweichung weiterbearbeitet wird.
        </p>
      </div>
      <div className="mt-4 flex flex-1 flex-col gap-3">
        <div
          role="group"
          aria-label="Review-Aktion"
          className="grid gap-2"
        >
          {nextActions.map((value) => {
            const active = action === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                disabled={pending}
                onClick={() => setAction(active ? null : value)}
                className={
                  active
                    ? "min-h-9 rounded-md border border-emerald-600 bg-emerald-50 px-3 py-2 text-left text-sm font-medium text-emerald-800"
                    : "min-h-9 rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                }
              >
                {REVIEW_NEXT_ACTION_LABELS[value]}
              </button>
            );
          })}
        </div>
        <textarea
          aria-label="Kommentar zur Preisprüfung"
          aria-describedby="preispruefung-kommentar-hinweis"
          className="min-h-[6.5rem] w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400"
          value={comment}
          disabled={pending}
          maxLength={2000}
          placeholder={reviewCommentPlaceholder(action)}
          onChange={(event) => setComment(event.target.value)}
        />
        <p
          id="preispruefung-kommentar-hinweis"
          className={
            missingRequiredComment
              ? "min-h-8 text-xs font-medium leading-relaxed text-amber-700"
              : "min-h-8 text-xs leading-relaxed text-slate-500"
          }
        >
          {missingRequiredComment
            ? reviewEditorHint(action)
            : action
              ? REVIEW_NEXT_ACTION_HELP[action]
              : reviewEditorHint(null)}
        </p>
        {action === "complaint" || action === "credit_expected" ? (
          <div className="rounded-md border border-slate-200 bg-white text-xs text-slate-600">
            <div className="flex items-center justify-between gap-2 p-2">
              <button
                type="button"
                aria-expanded={complaintTextOpen}
                onClick={() => setComplaintTextOpen((open) => !open)}
                className="flex items-center gap-1 font-medium text-slate-700 hover:text-slate-900"
              >
                <span aria-hidden="true">{complaintTextOpen ? "▾" : "▸"}</span>
                Text für E-Mail
              </button>
              <button
                type="button"
                onClick={() => void copyComplaintText()}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                {copied ? "Kopiert" : "Text kopieren"}
              </button>
            </div>
            {complaintTextOpen ? (
              <pre className="whitespace-pre-wrap border-t border-slate-200 p-2 font-sans text-xs leading-relaxed">
                {complaintText}
              </pre>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          disabled={!canSubmit}
          onClick={submitReview}
          className="h-9 rounded-md bg-emerald-600 px-3 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {pending
            ? "Speichere..."
            : advancesAfterSave
              ? "Speichern & weiter"
              : "Speichern"}
        </button>
        {errorMessage ? (
          <span className="text-sm text-rose-700">{errorMessage}</span>
        ) : null}
      </div>
    </form>
  );
}

function InlineArticleHistory({
  line,
  fromPath,
  projectByKey,
}: {
  line: BelegLineItem;
  fromPath: string;
  projectByKey: ReadonlyMap<string, ProjectSummary>;
}) {
  const articleGroupKey =
    line.article_group_key && line.article_detail_available
      ? line.article_group_key
      : null;
  const historyQuery = useQuery({
    queryKey: ["belege", "article-history", articleGroupKey],
    queryFn: () => fetchCompletePriceHistory(articleGroupKey ?? ""),
    enabled: Boolean(articleGroupKey),
  });
  const rows = historyQuery.data?.items ?? [];
  const currentRowVisible = rows.some((row) =>
    isCurrentHistoryRow(row, line),
  );
  const articleHref = articleGroupKey
    ? `/articles/${encodeURIComponent(articleGroupKey)}`
    : null;
  const historyScrollerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const scroller = historyScrollerRef.current;
    const currentRow = Array.from(scroller?.querySelectorAll<HTMLElement>('[aria-current="true"]') ?? []).find((row) => row.getClientRects().length > 0);
    if (!scroller || !currentRow) return;
    const centeredTop =
      currentRow.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop -
      scroller.clientHeight / 2 +
      currentRow.clientHeight / 2;
    scroller.scrollTop = Math.max(0, centeredTop);
  }, [articleGroupKey, line.beleg_id, line.line_pos, rows.length]);

  return (
    <section
      aria-labelledby="inline-article-history-heading"
      className="mt-6 border-t border-slate-200 pt-5"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <h3
            id="inline-article-history-heading"
            className="text-xs font-semibold uppercase tracking-wide text-slate-500"
          >
            Artikelhistorie
          </h3>
            <SectionInfo label="Artikelhistorie">
              Alle sicher zugeordneten Rechnungspositionen dieses Artikels.
            </SectionInfo>
          </div>
        </div>
        {articleHref ? (
          <Link
            to={articleHref}
            state={{ from: fromPath }}
            className="text-xs font-medium text-emerald-700 hover:underline"
          >
            Vollständige Artikelseite öffnen →
          </Link>
        ) : null}
      </div>

      {!articleGroupKey ? (
        <p className="mt-3 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-sm text-slate-600">
          Keine sicher zugeordnete Artikelhistorie verfügbar. Ähnlich bezeichnete
          Artikel werden nicht zugemischt.
        </p>
      ) : historyQuery.isLoading ? (
        <div
          role="status"
          className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500"
        >
          Lade Artikelhistorie …
        </div>
      ) : historyQuery.isError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-4 text-sm text-rose-700">
          <p>Artikelhistorie konnte nicht geladen werden.</p>
          <button
            type="button"
            onClick={() => void historyQuery.refetch()}
            className="mt-2 rounded-md border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-medium hover:bg-rose-100"
          >
            Erneut versuchen
          </button>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-sm text-slate-600">
          Für diesen sicher zugeordneten Artikel ist noch keine
          Transaktionshistorie verfügbar.
        </p>
      ) : (
        <>
          <div className="mt-3 max-w-full overflow-hidden rounded-md border border-slate-200 [contain:layout_paint]">
            <div
              ref={historyScrollerRef}
              className="max-h-96 max-w-full overflow-auto"
            >
              <HistoryTable
                key={articleGroupKey}
                rows={rows.map((row, index) => ({
                  id: `${row.invoice_doc_id ?? row.doc_number ?? "history"}-${row.line_pos ?? index}-${index}`,
                  data: row,
                  date: row.doc_date,
                  current: isCurrentHistoryRow(row, line),
                  document: <div>
                    <span className="font-mono text-xs">{row.doc_number ?? row.invoice_doc_id ?? "–"}</span>
                    {row.invoice_doc_id ? <div className="mt-1"><DocumentPdfActions docId={row.invoice_doc_id} docNumber={row.doc_number} linePos={row.line_pos} pageNo={row.page_no} sourceKind={row.source_kind} label="PDF" compact /></div> : null}
                    <HistoryExternalNumber value={row.external_number} />
                  </div>,
                  costCenter: <CostCenterValue projectKey={row.project_key} deliveryAddressDisplay={row.delivery_address_display} projectByKey={projectByKey} />,
                  supplier: row.supplier,
                  qty: row.qty,
                  uom: row.uom,
                  unitPrice: row.effective_unit_price ?? row.net_unit_price,
                  searchText: [row.doc_number, row.external_number, row.project_key, row.delivery_address_display, row.supplier, row.project_key ? projectByKey.get(row.project_key)?.display_name : null].filter(Boolean).join(" ").toLowerCase(),
                }))}
                columns={["date", "document", "costCenter", "supplier", "qty", "unitPrice"]}
                onExport={exportRows => downloadOfferCsv(historyCsv([
                  ["Datum", "Beleg", "ER-Nummer", "Kostenstelle", "Großhändler", "Menge", "Einheit", "Netto-Preis", "Aktuell geprüft"],
                  ...exportRows.map(row => [row.doc_date, row.doc_number, row.external_number, row.project_key ? projectByKey.get(row.project_key)?.display_name ?? row.project_key : row.delivery_address_display ?? "Nicht zugeordnet", row.supplier, historyCsvNumber(row.qty), row.uom, historyCsvNumber(row.effective_unit_price ?? row.net_unit_price), isCurrentHistoryRow(row, line) ? "Ja" : "Nein"]),
                ]), "beleg-preisverlauf.csv")}
                searchPlaceholder="Beleg, Kostenstelle oder Großhändler …"
                minWidthClass="min-w-[760px]"
                hideBelowXl={[]}
              />
            </div>
          </div>
          {!currentRowVisible ? (
            <p className="mt-2 text-xs text-amber-700">
              Die aktuell geprüfte Position ist in dieser Rechnungshistorie nicht
              enthalten.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

function reviewEventLabel(event: BelegPriceAnomalyReviewEvent): string {
  if (event.action) return REVIEW_ACTION_LABELS[event.action];
  if (event.status === "open") return reviewStatusLabel("open");
  if (event.status === "in_review") return "In Bearbeitung";
  return "Abgeschlossen";
}

type ReviewNextAction =
  | "comment"
  | "complaint"
  | "credit_expected"
  | "write_off"
  | "credit_resolved"
  | "reopen";

function isAdvancingReviewAction(
  action: BelegPriceAnomalyAction | null,
): boolean {
  return (
    action === "complaint" ||
    action === "credit_expected" ||
    action === "write_off" ||
    action === "credit_resolved"
  );
}

function availableReviewActionsForLine(row: BelegLineItem): ReviewNextAction[] {
  const status = row.price_anomaly_status ?? "open";
  if (status === "resolved") return ["comment", "reopen"];
  if (status === "in_review") {
    if (row.price_anomaly_action === "credit_expected") {
      return ["comment", "credit_resolved", "write_off", "reopen"];
    }
    return ["comment", "credit_expected", "credit_resolved", "write_off"];
  }
  return ["comment", "complaint", "credit_expected", "write_off"];
}

function reviewCommentPlaceholder(action: ReviewNextAction | null): string {
  if (action === "comment") return "Neue Notiz zu dieser Position";
  if (action === "write_off") return "Begründung für das Ausbuchen";
  if (action) return "Optionaler Kommentar zu dieser Entscheidung";
  return "Zuerst eine Aktion auswählen";
}

function reviewEditorHint(action: ReviewNextAction | null): string {
  if (action === "comment") {
    return "Für „Nur Kommentar“ ist eine neue Notiz erforderlich.";
  }
  if (action === "write_off") {
    return "Für „Ausbuchen“ ist eine kurze Begründung erforderlich.";
  }
  if (action) return "Ein ergänzender Kommentar ist optional.";
  return "Bitte zuerst genau eine Aktion auswählen.";
}

function formatReviewOverpay(
  value: string | number | null | undefined,
): string {
  return toNumber(value) === null ? "nicht verfügbar" : formatEuro(value);
}

function complaintTextForLine(row: BelegLineItem): string {
  const article =
    row.primary_article_code ?? row.article_group_key ?? "ohne Artikelnummer";
  const description =
    cleanDescription(row.description ?? "") ?? row.description ?? "ohne Bezeichnung";
  return [
    "Bitte um Prüfung folgender Preisabweichung:",
    "",
    `Beleg: ${row.beleg_id}`,
    `Position: ${row.line_pos ?? "–"}`,
    `Artikel: ${article}`,
    `Bezeichnung: ${description}`,
    `Menge: ${formatQty(row.qty)}${row.uom ? ` ${row.uom}` : ""}`,
    `Berechneter Netto-Einzelpreis: ${formatEuro(
      row.effective_unit_price ?? row.net_unit_price,
    )}`,
    `${baselineKindLabel(row.canonical_baseline_kind)}: ${formatEuro(
      row.canonical_baseline_price,
    )}`,
    `Abweichung: ${formatPercent(row.canonical_delta_pct)}`,
    `Mehrkosten: ${formatReviewOverpay(row.overpay_abs)}`,
    "",
    "Bitte um Korrektur bzw. Gutschrift, falls der Preis nicht vereinbart war.",
  ].join("\n");
}

const REVIEW_NEXT_ACTION_LABELS: Record<ReviewNextAction, string> = {
  comment: "Nur Kommentar",
  complaint: "Klärung anfragen",
  credit_expected: "Gutschrift erwartet",
  write_off: "Ausbuchen",
  credit_resolved: "Gutschrift erhalten",
  reopen: "Wieder öffnen",
};

const REVIEW_NEXT_ACTION_HELP: Record<ReviewNextAction, string> = {
  comment:
    "Die Notiz wird zum Verlauf ergänzt. Der fachliche Prüfstatus bleibt unverändert.",
  complaint:
    "Eine Rückfrage läuft. TruuCost dokumentiert sie, versendet aber keine E-Mail.",
  credit_expected:
    "Die Gutschrift wurde angefordert oder zugesagt und bleibt bis zur Zuordnung offen.",
  write_off:
    "Die Preisabweichung wird akzeptiert und diese Prüfung abgeschlossen.",
  credit_resolved:
    "Sie dokumentieren den Abschluss mit einer erhaltenen Gutschrift. Dieser Status verknüpft noch keinen Gutschrift-Beleg.",
  reopen: "Die abgeschlossene Prüfung wird erneut zur Bearbeitung geöffnet.",
};

const REVIEW_ACTION_LABELS: Record<BelegPriceAnomalyAction, string> = {
  comment: "Kommentar",
  open: reviewStatusLabel("open"),
  resolve: "Abgeschlossen (ohne Angabe)",
  reopen: "Wieder geöffnet",
  complaint: "Klärung angefragt",
  credit_expected: "Gutschrift erwartet",
  write_off: "Ausgebucht",
  credit_resolved: "Gutschrift erhalten",
};


function LineReviewStatusBadge({
  value,
  action,
  size = "compact",
}: {
  value: BelegPriceAnomalyStatus;
  action?: BelegPriceAnomalyAction | null;
  size?: "compact" | "prominent";
}) {
  const labels: Record<BelegPriceAnomalyStatus, string> = {
    open: reviewStatusLabel("open"),
    in_review: "In Bearbeitung (ohne Angabe)",
    resolved: "Abgeschlossen (ohne Angabe)",
  };
  const actionLabel = action ? REVIEW_ACTION_LABELS[action] : null;
  const tone =
    value === "open"
      ? "bg-rose-50 text-rose-700"
      : value === "in_review"
        ? "bg-amber-50 text-amber-700"
        : "bg-emerald-50 text-emerald-700";
  const label = actionLabel ?? labels[value];
  const sizeClass =
    size === "prominent"
      ? "rounded-md px-2.5 py-1 text-sm font-semibold leading-snug"
      : "rounded px-2 py-0.5 text-xs font-medium leading-tight";
  return (
    <span
      data-testid={size === "prominent" ? "current-review-status" : undefined}
      className={`inline-flex max-w-full items-center text-left ${sizeClass} ${tone}`}
    >
      {size === "prominent" ? (
        <span className="sr-only">Aktueller Status: </span>
      ) : null}
      {label}
    </span>
  );
}

function Th({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      scope="col"
      className={`px-3 py-2 font-medium ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

function countSources(beleg: BelegRow | null): Record<string, number> {
  if (!beleg) return { orders: 0, ugl: 0 };
  return {
    orders: toNumber(beleg.matched_order_count) ?? 0,
    ugl: toNumber(beleg.ugl_line_count) ?? 0,
  };
}

function projectHrefFromKey(projectKey: string | null): string | null {
  return costCenterHrefFromProjectKey(projectKey);
}

function projectLabelFromKey(projectKey: string | null): string {
  if (!projectKey) return "Projekt";
  if (projectKey.startsWith("code:")) {
    const code = projectKey.slice("code:".length).trim();
    return code ? `Projekt ${code}` : "Projekt";
  }
  return displayCostCenterKey("project", projectKey);
}

function customerReferenceHrefFromKey(reference: string | null): string | null {
  const key = reference?.trim();
  if (!key) return null;
  if (!key.startsWith("customer:") && !key.startsWith("ref:")) {
    return null;
  }
  return costCenterHref("customer_reference", key);
}

function baselineKindLabel(kind: "12m" | "24m" | "alltime" | null): string {
  switch (kind) {
    case "12m":
      return "Schnitt 12 M. davor";
    case "24m":
      return "Schnitt 24 M. davor";
    case "alltime":
      return "Früherer Gesamtschnitt";
    default:
      return "Keine frühere Referenz";
  }
}

type ReviewError = {
  lineKey: string;
  message: string;
};

type ReviewSuccess = {
  savedLinePos: string;
  nextLinePos: string | null;
  allCompleted: boolean;
};

function reviewSuccessMessage(success: ReviewSuccess): string {
  if (success.nextLinePos) {
    return `Position ${success.savedLinePos} gespeichert. Weiter mit Position ${success.nextLinePos}.`;
  }
  if (success.allCompleted) {
    return `Position ${success.savedLinePos} gespeichert. Alle Preisprüfungen dieses Belegs sind bearbeitet.`;
  }
  return `Position ${success.savedLinePos} gespeichert.`;
}

function isCurrentHistoryRow(
  row: PriceHistoryRow,
  line: BelegLineItem,
): boolean {
  return (
    row.invoice_doc_id === line.beleg_id &&
    String(row.line_pos ?? "") === String(line.line_pos ?? "")
  );
}

function focusPriceReviewPanel(): void {
  const panel = document.getElementById("preispruefung-editor");
  panel?.focus({ preventScroll: true });
  panel?.scrollIntoView({ block: "start" });
}

function lineReviewKey(row: BelegLineItem): string {
  return lineReviewKeyFromValues(row.beleg_id, row.line_pos ?? "");
}

function lineReviewKeyFromValues(
  belegId: string,
  linePos: string | number,
): string {
  return `${belegId}::${String(linePos)}`;
}
